// @ts-nocheck
import { Stack, Title, Text, SimpleGrid, Code } from '@mantine/core';
import { Card } from '../../../../components/ui';
import {
  PieChartWidget,
  DonutChartWidget,
} from '../../../../components/advanced/Widgets';
import { mockPieChartData } from '../../mockData/widgets';

export default function PieChartWidgetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Pie & Donut Chart Widgets</Title>
        <Text c="dimmed" size="sm">
          Proportional distribution visualization widgets. Perfect for showing percentage
          breakdowns and category distributions at a glance.
        </Text>
      </Stack>

      {/* Basic Pie Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Pie Chart</Title>
          <Text size="sm" c="dimmed">
            Classic pie chart for visualizing proportional data distribution.
          </Text>

          <PieChartWidget
            title="User Distribution"
            subtitle="By user type"
            data={mockPieChartData}
            variant="pie"
            showPercentages
            showLegend
            height={350}
            showExport
          />

          <Code block>
{`<PieChartWidget
  title="User Distribution"
  data={distributionData}
  variant="pie"
  showPercentages
  showLegend
  showExport
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Basic Donut Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Donut Chart</Title>
          <Text size="sm" c="dimmed">
            Donut chart variant with center label for displaying total or summary information.
          </Text>

          <DonutChartWidget
            title="User Breakdown"
            subtitle="Current distribution"
            data={mockPieChartData}
            centerLabel="447 Total"
            showPercentages
            showLegend
            height={350}
            showExport
          />

          <Code block>
{`<DonutChartWidget
  title="User Breakdown"
  data={userData}
  centerLabel="447 Total"
  showPercentages
  showLegend
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Side-by-Side Comparison */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Pie vs Donut Comparison</Title>
          <Text size="sm" c="dimmed">
            Same data shown in both variants for design comparison.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <PieChartWidget
              title="Pie Chart Variant"
              data={mockPieChartData}
              variant="pie"
              showPercentages
              showLegend
              height={300}
            />

            <DonutChartWidget
              title="Donut Chart Variant"
              data={mockPieChartData}
              centerLabel="Total: 447"
              showPercentages
              showLegend
              height={300}
            />
          </SimpleGrid>

          <Code block>
{`// Pie Chart
<PieChartWidget
  data={data}
  variant="pie"
  showPercentages
/>

// Donut Chart
<DonutChartWidget
  data={data}
  centerLabel="Total: 447"
  showPercentages
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Without Percentages */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Clean Display (No Percentages)</Title>
          <Text size="sm" c="dimmed">
            Simplified view without percentage labels on segments.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <PieChartWidget
              title="Clean Pie Chart"
              subtitle="Values shown in legend only"
              data={mockPieChartData}
              variant="pie"
              showPercentages={false}
              showLegend
              height={300}
            />

            <DonutChartWidget
              title="Clean Donut Chart"
              subtitle="Center label with legend"
              data={mockPieChartData}
              centerLabel="447"
              showPercentages={false}
              showLegend
              height={300}
            />
          </SimpleGrid>

          <Code block>
{`<DonutChartWidget
  data={data}
  centerLabel="447"
  showPercentages={false}  // Hide segment labels
  showLegend  // Show values in legend
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Custom Sizing */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Custom Sizing</Title>
          <Text size="sm" c="dimmed">
            Adjust chart height and radius for different layouts.
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            <DonutChartWidget
              title="Small"
              data={mockPieChartData.slice(0, 4)}
              centerLabel="275"
              showPercentages
              height={250}
              innerRadius={50}
              outerRadius={80}
            />

            <DonutChartWidget
              title="Medium"
              data={mockPieChartData.slice(0, 4)}
              centerLabel="275"
              showPercentages
              height={300}
              innerRadius={60}
              outerRadius={100}
            />

            <DonutChartWidget
              title="Large"
              data={mockPieChartData.slice(0, 4)}
              centerLabel="275"
              showPercentages
              height={350}
              innerRadius={70}
              outerRadius={120}
            />
          </SimpleGrid>

          <Code block>
{`<DonutChartWidget
  data={data}
  height={350}
  innerRadius={70}  // Hole size
  outerRadius={120}  // Chart size
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Market Share Analysis</Text>
              <Text size="xs" c="dimmed">
                Visualize market share distribution across competitors or product categories.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Budget Allocation</Text>
              <Text size="xs" c="dimmed">
                Show how budget is distributed across departments, projects, or expense categories.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>User Demographics</Text>
              <Text size="xs" c="dimmed">
                Display user distribution by role, location, subscription type, or other attributes.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Task Status Overview</Text>
              <Text size="xs" c="dimmed">
                Quick snapshot of task completion status (completed, in-progress, pending).
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
